import { IFieldInfoProperties, isRequired, isVisible } from "@components/smart/FieldInfo";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import BindingContext, { IEntity } from "@odata/BindingContext";
import { isObjectEmpty } from "@utils/general";
import { anyPartStartsWithAccentsInsensitive } from "@utils/string";

import { FormMode, ValueType } from "../../enums";
import { FormStorage, IFormStorageDefaultCustomData, IGroupStatus } from "./FormStorage";

interface IVirtualizedFormStorageCustomData extends IFormStorageDefaultCustomData {
    searchTerm: string; // custom data field to store search term for list group summary item
}

export type TSmartVirtualizedFormStorage = FormStorage<IEntity, IVirtualizedFormStorageCustomData>;
export const CUSTOMIZE_FIELDS_ACTION = "customize_fields";

/**
 *  **** RULES for FIELDS VISIBILITY in VIRTUALIZED FORM ****
 *
 * We will have settings for which fields are currently visible. This is initialized at the start when the form is loaded.
 * These will be required fields plus optional fields that already have a value.
 * The ability to remove a field will be controlled by the current value of "isRequired".
 * If an optional field is removed, its value will be cleared and the field will be removed from the list of visible fields.
 * If a field becomes required, it will be automatically added to the list of visible fields.
 * If a field becomes optional, it will remain visible until the user removes it manually (or clears its value).
 * If a field becomes visible, then if it is required or has a value, it will be displayed automatically. In other cases, it will only appear under the "Add" button (the "Add" button may become visible in this way if it is the first addable field).
 * If a field becomes hidden, it will disappear immediately from wherever it is shown. The "Add" button may also disappear.
 */

function hasFieldValue(storage: TSmartVirtualizedFormStorage, fieldBc: BindingContext): boolean {
    const val = storage.getValue(fieldBc, { useDirectValue: false });
    // if there are 3 possibilities, there cannot be boolean type (https://solitea-cz.atlassian.net/browse/DEV-44135)
    return /*isBooleanType(fieldBc.getProperty()) ? val === true : */ !isObjectEmpty(val);
}

function getFieldVisibilityStatus(storage: TSmartVirtualizedFormStorage, groupId: string, groupPath: string): IGroupStatus {
    const status = storage.getGroupStatus(groupId) ?? {};
    if (!status.visibleFields) {
        status.visibleFields = {};
    }
    if (!status.visibleFields[groupPath]) {
        status.visibleFields[groupPath] = {};
    }

    return status;
}

/**
 * Checks if a field is currently visible based on its isVisible callback and status
 * in the visibleFields - if added by user explicitly.
 * @param storage
 * @param groupBc
 * @param groupDef
 * @param fieldId
 * @param fieldBc
 */
export function isFieldCurrentlyVisible(storage: TSmartVirtualizedFormStorage, groupBc: BindingContext, groupDef: IFormGroupDef, fieldId: string, fieldBc: BindingContext): boolean {
    const info = storage.getInfo(fieldBc);
    if (!info) {
        return false;
    }

    const visibilityValue = isVisible({ storage, bindingContext: fieldBc, info, context: storage.context });

    if (visibilityValue === false) {
        return false;
    }

    if (shouldFieldBeVisible(storage, fieldBc)) {
        return true;
    }

    const groupPath = groupBc.getNavigationPath(false);
    const { visibleFields } = getFieldVisibilityStatus(storage, groupDef.id, groupPath);
    return visibleFields[groupPath][fieldId] === true;
}

/**
 * Determines whether a field should be visible based on its required status and whether it has a value.
 * -> when detected, field should be automatically added to the visibleFields group status and rendered as visible
 *  /if required, then not-removable/
 * @param storage
 * @param fieldBc
 */
export function shouldFieldBeVisible(storage: TSmartVirtualizedFormStorage, fieldBc: BindingContext): boolean {
    const info = storage.getInfo(fieldBc);

    const _isAudit = storage.formMode === FormMode.AuditTrail;
    if (_isAudit) {
        // in audit trail mode, we only show fields that have a value
        return hasFieldValue(storage, fieldBc);
    }

    const _isRequired = isRequired({ storage, bindingContext: fieldBc, info });

    return _isRequired || hasFieldValue(storage, fieldBc);
}

export function getAddableFields(storage: TSmartVirtualizedFormStorage, baseBc: BindingContext, def: IFormGroupDef): string[] {
    const flattenedRows = (def.rows || []).flat();

    return flattenedRows
        .filter(fieldDef => {
            const bc = baseBc.navigate(fieldDef.id);
            const info = storage.getInfo(bc);
            const _isVisible = isVisible({ storage, bindingContext: bc, info, context: storage.context });
            const _isRequired = isRequired({ storage, bindingContext: bc, info });

            return _isVisible && !_isRequired;
        })
        .map(fieldDef => fieldDef.id);
}

export function isFieldMatchingSearchTerm(field: IFieldInfoProperties, searchTerm: string): boolean {
    const { label, metadata } = field;
    return anyPartStartsWithAccentsInsensitive(label, searchTerm, ValueType.String)
        || (metadata?.id && anyPartStartsWithAccentsInsensitive(metadata.id.toString(), searchTerm, ValueType.Number));
}

export function getVisibleFields(storage: TSmartVirtualizedFormStorage, groupBc: BindingContext, groupDef: IFormGroupDef): string[] {
    return getAddableFields(storage, groupBc, groupDef).filter(fieldId =>
        isFieldCurrentlyVisible(storage, groupBc, groupDef, fieldId, groupBc.navigate(fieldId)));
}

export function setFieldVisibility(storage: TSmartVirtualizedFormStorage, fieldBc: BindingContext, isVisible = false): boolean {
    const groupId = storage._getGroupKeyFromBc(fieldBc);
    let groupBc = fieldBc.getParent();
    while (!groupBc.isCollection() && !groupBc.isRoot()) {
        groupBc = groupBc.getParent();
    }

    const fieldId = fieldBc.getNavigationPath(true, groupBc);
    const groupPath = groupBc.getNavigationPath(false);
    const status = getFieldVisibilityStatus(storage, groupId, groupPath);

    if (status.visibleFields[groupPath][fieldId] === isVisible) {
        return false;
    }

    status.visibleFields[groupPath][fieldId] = isVisible;
    storage.setGroupStatus(status, groupId);
    return true;
}

export function setMassFieldsVisibility(storage: TSmartVirtualizedFormStorage, groupBc: BindingContext, groupDef: IFormGroupDef, selectedRows: string[]): void {
    const groupPath = groupBc.getNavigationPath(false);
    const status = getFieldVisibilityStatus(storage, groupDef.id, groupPath);
    getAddableFields(storage, groupBc, groupDef).forEach(rowId => {
        const bc = groupBc.navigate(rowId);
        const isSelected = selectedRows.includes(rowId);
        if (!isSelected) {
            storage.clearValue(bc);
            storage.addActiveFieldsFromAffectedFields(bc);
        }
        status.visibleFields[groupPath][rowId] = isSelected;
    });
    storage.setGroupStatus(status, groupDef.id);
}
